import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function memberLabel(m) {
  if (!m) return '—';
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return `${name || '—'} (${m.affiliate_code || '—'})`;
}

const PAGE_SIZE = 50;

export default function AuditLogs() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const [actionFilter, setActionFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [authLoading, user, navigate]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('limit', PAGE_SIZE);
      params.set('offset', offset);
      if (actionFilter.trim()) params.set('action', actionFilter.trim());
      if (startDate) params.set('start_date', `${startDate}T00:00:00`);
      if (endDate) params.set('end_date', `${endDate}T23:59:59`);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/audit-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load audit logs');
      }
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [token, offset, actionFilter, startDate, endDate]);

  useEffect(() => {
    if (!user || user.role !== 'superadmin') return;
    fetchLogs();
  }, [user, fetchLogs]);

  const applyFilters = () => {
    setOffset(0);
    fetchLogs();
  };

  const clearFilters = () => {
    setActionFilter('');
    setStartDate('');
    setEndDate('');
    setOffset(0);
  };

  if (!user || user.role !== 'superadmin') {
    return null;
  }

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-6xl mx-auto">
        <h1 className="text-headline-lg font-bold mb-6" style={{ color: '#c9a961' }}>
          Audit Logs
        </h1>

        <div className="border rounded-lg p-4 mb-4 flex flex-wrap items-end gap-3" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <div>
            <label className="block text-body-xs mb-1" style={{ color: '#999999' }}>Action contains</label>
            <input
              type="text"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              placeholder="e.g. release_team_bonus"
              className="px-3 py-2 rounded-lg border text-body-sm"
              style={{ backgroundColor: '#152214', borderColor: '#3d5a4a', color: '#c9a961' }}
            />
          </div>
          <div>
            <label className="block text-body-xs mb-1" style={{ color: '#999999' }}>From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 rounded-lg border text-body-sm"
              style={{ backgroundColor: '#152214', borderColor: '#3d5a4a', color: '#c9a961' }}
            />
          </div>
          <div>
            <label className="block text-body-xs mb-1" style={{ color: '#999999' }}>To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 rounded-lg border text-body-sm"
              style={{ backgroundColor: '#152214', borderColor: '#3d5a4a', color: '#c9a961' }}
            />
          </div>
          <button
            type="button"
            onClick={applyFilters}
            className="px-4 py-2 rounded-lg font-medium"
            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
          >
            Apply
          </button>
          <button
            type="button"
            onClick={clearFilters}
            className="px-4 py-2 rounded-lg font-medium border"
            style={{ backgroundColor: 'transparent', color: '#c9a961', borderColor: '#c9a961' }}
          >
            Clear
          </button>
        </div>

        {error && (
          <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
            {error}
          </div>
        )}

        {loading ? (
          <p style={{ color: '#b8a68f' }}>Loading...</p>
        ) : logs.length === 0 ? (
          <div className="border rounded-lg p-6 text-center" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <p style={{ color: '#b8a68f' }}>No audit log entries match these filters.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                <thead>
                  <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                    <th className="p-2">Date</th>
                    <th className="p-2">Action</th>
                    <th className="p-2">Admin</th>
                    <th className="p-2">Affected User</th>
                    <th className="p-2">Status</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <React.Fragment key={log.id}>
                      <tr className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                        <td className="p-2">{new Date(log.created_at).toLocaleString()}</td>
                        <td className="p-2" style={{ color: '#c9a961' }}>{log.action}</td>
                        <td className="p-2">{memberLabel(log.admin)}</td>
                        <td className="p-2">{memberLabel(log.user)}</td>
                        <td className="p-2">
                          <span
                            className="px-2 py-1 rounded text-body-xs font-medium"
                            style={{
                              backgroundColor: log.status === 'success' ? 'rgba(201,169,97,0.2)' : 'rgba(201,73,97,0.15)',
                              color: log.status === 'success' ? '#c9a961' : '#c94961',
                            }}
                          >
                            {log.status || '—'}
                          </span>
                        </td>
                        <td className="p-2">
                          <button
                            type="button"
                            onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                            className="px-3 py-1 rounded font-medium text-body-xs border"
                            style={{ backgroundColor: 'transparent', color: '#c9a961', borderColor: '#c9a961' }}
                          >
                            {expandedId === log.id ? 'Hide' : 'Details'}
                          </button>
                        </td>
                      </tr>
                      {expandedId === log.id && (
                        <tr className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                          <td colSpan={6} className="p-3">
                            <pre
                              className="text-body-xs overflow-x-auto p-3 rounded"
                              style={{ backgroundColor: '#152214', color: '#b8a68f', border: '1px solid #3d5a4a' }}
                            >
                              {JSON.stringify(log.details, null, 2)}
                              {log.error_message ? `\n\nError: ${log.error_message}` : ''}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-4">
              <p className="text-body-xs" style={{ color: '#999999' }}>
                Page {page} of {totalPages} ({total} total)
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={offset === 0}
                  className="px-4 py-2 rounded-lg font-medium border disabled:opacity-50"
                  style={{ backgroundColor: 'transparent', color: '#c9a961', borderColor: '#c9a961' }}
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={offset + PAGE_SIZE >= total}
                  className="px-4 py-2 rounded-lg font-medium border disabled:opacity-50"
                  style={{ backgroundColor: 'transparent', color: '#c9a961', borderColor: '#c9a961' }}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
